import { useMemo, useState, useEffect } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../components/ui/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faStore,
  faUser,
  faPlus,
  faBuilding,
  faEnvelope,
  faPhone,
  faCalendarAlt,
  faFilter,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import AddSeller from "./shop/AddSeller";
import SellerDetails from "./shop/SellerDetails";
import SearchBar from "../../components/ui/SearchBar";
import TableWrapper from "../../components/ui/TableWrapper";
import EmptyState from "../../components/ui/EmptyState";
import Pagination from "../../components/ui/Pagination";
import { useAuth, useShops } from "../../hooks";
import { useMyShopsSellers } from "../../hooks/useShopSellers";
import { toastError } from "../../utils/toast";

const ManageSellerO = () => {
  const { hasPermission } = useAuth();

  const canManageSellers =
    (hasPermission(["manage_shop_modules"]) ||
      hasPermission(["manage_business_seller"])) &&
    hasPermission(["manage_user_account"]);

  const {
    sellers: rawSellers,
    pagination,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    shopFilter,
    setShopFilter,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    refresh,
  } = useMyShopsSellers({ per_page: 10 });

  const { shops } = useShops({ per_page: 1000 });

  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);

  // Advanced Filter Modal State
  const [isAdvFilterOpen, setIsAdvFilterOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    startDate: "",
    endDate: "",
    shopFilter: "",
  });

  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  // Debounce search to prevent UI reload on every keystroke
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(localSearchTerm);
      setCurrentPage(1);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [localSearchTerm, setSearchTerm, setCurrentPage]);

  const safeShops = useMemo(() => (Array.isArray(shops) ? shops : []), [shops]);

  const sellers = useMemo(() => {
    if (!Array.isArray(rawSellers)) return [];

    return rawSellers.map((item) => ({
      id: item.shop_saller_id || item.id,
      shop_saller_id: item.shop_saller_id,
      shopId: item.shopId,
      username: item.username || item.user?.username || "N/A",
      seller_name:
        item.user?.full_name || item.full_name || item.seller_name || "N/A",
      name: item.user?.full_name || item.full_name || item.seller_name || "N/A",
      full_name: item.user?.full_name || item.full_name || "N/A",
      phone: item.user?.phone || item.phone || "N/A",
      email: item.user?.email || item.email || "N/A",
      shopName: item.shop?.shop_name || item.shopName || "-",
      added_date: item.added_date
        ? new Date(item.added_date).toISOString().split("T")[0]
        : "N/A",
      joinedDate: item.added_date
        ? new Date(item.added_date).toISOString().split("T")[0]
        : "N/A",
      status: item.status === "active" ? "Active" : "Inactive",
      created_at: item.created_at || item.user?.created_at,
      updated_at: item.updated_at || item.user?.updated_at,
      user: item.user,
      shop: item.shop,
    }));
  }, [rawSellers]);

  const filteredSellers = useMemo(() => {
    let result = sellers;

    if (isFilterApplied && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((seller) => {
        if (!seller.added_date || seller.added_date === "N/A") return false;
        const addedDate = new Date(seller.added_date);
        return addedDate >= start && addedDate <= end;
      });
    }

    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase().trim();
      result = result.filter(
        (s) =>
          s.seller_name?.toLowerCase().includes(search) ||
          s.email?.toLowerCase().includes(search) ||
          s.phone?.toLowerCase().includes(search) ||
          s.username?.toLowerCase().includes(search) ||
          s.shopName?.toLowerCase().includes(search),
      );
    }

    if (shopFilter && shopFilter !== "all") {
      result = result.filter((s) => s.shopId === shopFilter);
    }

    return result;
  }, [sellers, searchTerm, shopFilter, startDate, endDate, isFilterApplied]);

  const totalItems = pagination?.total ?? filteredSellers.length;
  const totalPages = pagination?.last_page ?? 1;
  const startIndex = (currentPage - 1) * pageSize;

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleAddSellerSuccess = async () => {
    setIsAddModalOpen(false);
    try {
      await refresh();
    } catch {
      toastError("Seller added but list could not refresh. Please reload.");
    }
  };

  // --- Advanced Filter Handlers ---
  const openAdvFilter = () => {
    setTempFilters({
      startDate: startDate || "",
      endDate: endDate || "",
      shopFilter: shopFilter || "",
    });
    setIsAdvFilterOpen(true);
  };

  const applyAdvFilters = () => {
    setStartDate(tempFilters.startDate);
    setEndDate(tempFilters.endDate);
    setShopFilter(
      tempFilters.shopFilter === "all" ? "" : tempFilters.shopFilter,
    );
    setIsFilterApplied(
      !!(
        tempFilters.startDate ||
        tempFilters.endDate ||
        tempFilters.shopFilter !== "all"
      ),
    );
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  const clearAdvFilters = () => {
    setTempFilters({
      startDate: "",
      endDate: "",
      shopFilter: "",
    });
    setStartDate("");
    setEndDate("");
    setShopFilter("");
    setIsFilterApplied(false);
    setCurrentPage(1);
    setIsAdvFilterOpen(false);
  };

  const MobileSellerCard = ({ seller, index }) => (
    <div className="bg-white border-2 border-blue-900 rounded-md p-4 shadow-sm transition-all duration-200">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-white font-medium bg-blue-900 px-2 rounded-full">
              {startIndex + index + 1}
            </span>
            <h4 className="text-sm font-semibold text-gray-900 truncate">
              {seller.seller_name}
            </h4>
          </div>

          <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1.5">
            <FontAwesomeIcon
              icon={faEnvelope}
              className="h-3 w-3 text-gray-600"
            />
            <span className="font-medium text-sm">Email:</span>
            <span className="text-gray-600 truncate text-sm">
              {seller.email}
            </span>
          </div>

          <div className="flex items-center gap-1.5 text-xs text-gray-600 mb-1.5">
            <FontAwesomeIcon icon={faPhone} className="h-3 w-3 text-gray-600" />
            <span className="font-medium text-sm">Phone:</span>
            <span className="text-gray-600 text-sm">{seller.phone}</span>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-1.5 pt-1.5 border-t border-gray-100">
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <FontAwesomeIcon
                icon={faBuilding}
                className="h-3 w-3 text-gray-600"
              />
              <span className="font-medium text-sm">Shop:</span>
              <span className="text-gray-700 text-sm truncate max-w-[120px]">
                {seller.shopName}
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <FontAwesomeIcon
                icon={faCalendarAlt}
                className="h-3 w-3 text-gray-600"
              />
              <span className="font-medium text-sm">Joined:</span>
              <span className="text-blue-900 font-medium text-sm">
                {formatDate(seller.added_date)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-1.5 shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedSeller(seller);
              setIsViewModalOpen(true);
            }}
            title="View Seller"
            className="gap-1 border-green-600 text-green-600 hover:bg-green-50 hover:border-green-700 px-2.5 py-1 h-8 text-xs"
          >
            <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
            <span className="hidden xs:inline">View</span>
          </Button>
        </div>
      </div>
    </div>
  );

  if (loading && sellers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px]">
        <FontAwesomeIcon
          icon={faUser}
          className="h-10 w-10 text-blue-600 animate-pulse"
        />
        <p className="mt-4 text-gray-600 text-sm">Loading sellers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[300px]">
        <p className="text-red-600 text-sm">
          Failed to load sellers. Please try again.
        </p>
        <Button
          onClick={() => refresh()}
          className="mt-4 bg-blue-800 hover:bg-blue-900 text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6 max-w-8xl mx-auto px-3 sm:px-4 lg:px-0">
      <Card className="bg-white border border-gray-200 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <FontAwesomeIcon
                icon={faUser}
                className="text-blue-800 h-4 w-4 sm:h-5 sm:w-5"
              />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900">
                Manage sellers
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                Manage sellers across {safeShops.length} shops
              </p>
            </div>
          </div>
          {canManageSellers && (
            <Button
              onClick={() => setIsAddModalOpen(true)}
              className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add seller
            </Button>
          )}
        </div>
      </Card>

      {/* Main Toolbar: Search Left, Advanced Filter Right */}
      <div className="flex flex-col sm:flex-row gap-3">
        <SearchBar
          value={localSearchTerm}
          onChange={(value) => setLocalSearchTerm(value)}
          placeholder="Search by name, email, phone, or shop..."
          className="flex-1"
        />
        <Button
          onClick={openAdvFilter}
          className="gap-2 bg-blue-800 hover:bg-blue-900 text-white w-full sm:w-auto px-6"
        >
          <FontAwesomeIcon icon={faFilter} />
          Advanced Filter
          {isFilterApplied && (
            <span className="ml-1 w-2 h-2 rounded-full bg-orange-400"></span>
          )}
        </Button>
      </div>

      <div className="hidden md:block">
        <TableWrapper
          title="sellers"
          totalItems={totalItems}
          currentPage={currentPage}
          totalPages={totalPages}
          className="bg-white border border-gray-200 shadow-sm"
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 hover:bg-gray-50 border-b-2 border-gray-200">
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  #
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Seller
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden md:table-cell">
                  Phone
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden lg:table-cell">
                  Shop
                </th>
                <th className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4 hidden sm:table-cell">
                  Joined
                </th>
                <th className="text-center text-xs font-semibold text-gray-600 uppercase tracking-wider py-2 sm:py-3 px-2 sm:px-4">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredSellers.length > 0 ? (
                filteredSellers.map((seller, i) => {
                  const globalIndex = startIndex + i + 1;
                  return (
                    <tr
                      key={seller.id || seller.shop_saller_id}
                      className="hover:bg-blue-50/50 transition-colors"
                    >
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-xs text-gray-600 font-medium">
                        {globalIndex}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4">
                        <div>
                          <p className="font-medium text-gray-800 text-sm">
                            {seller.seller_name}
                          </p>
                          <p className="text-xs text-gray-600">
                            {seller.email}
                          </p>
                        </div>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-600 hidden md:table-cell">
                        {seller.phone}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-600 hidden lg:table-cell">
                        <div className="flex items-center gap-1.5">
                          <FontAwesomeIcon
                            icon={faBuilding}
                            className="h-3 w-3 text-gray-600"
                          />
                          <span className="truncate max-w-[120px]">
                            {seller.shopName}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-sm text-gray-600 hidden sm:table-cell">
                        {formatDate(seller.added_date)}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4">
                        <div className="flex gap-1 sm:gap-2 justify-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 sm:h-8 sm:w-8 p-0 text-green-600 hover:bg-green-50"
                            onClick={() => {
                              setSelectedSeller(seller);
                              setIsViewModalOpen(true);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faEye}
                              className="h-3 w-3 sm:h-4 sm:w-4"
                            />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-6 sm:py-8">
                    <EmptyState
                      title="No sellers found"
                      message={
                        searchTerm || shopFilter || isFilterApplied
                          ? "Try adjusting your search or filters"
                          : canManageSellers
                            ? "Add your first seller to get started"
                            : "No sellers yet"
                      }
                    />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TableWrapper>
      </div>

      <div className="md:hidden">
        {filteredSellers.length > 0 ? (
          <>
            <div className="flex items-center justify-between text-xs text-gray-600 mb-3 px-1">
              <span>
                Showing {startIndex + 1}-
                {Math.min(startIndex + pageSize, totalItems)} of {totalItems}{" "}
                sellers
              </span>
              <span>
                Page {currentPage} of {totalPages || 1}
              </span>
            </div>
            <div className="space-y-3">
              {filteredSellers.map((seller, i) => (
                <MobileSellerCard
                  key={seller.id || seller.shop_saller_id}
                  seller={seller}
                  index={i}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <EmptyState
              title="No sellers found"
              message={
                searchTerm || shopFilter || isFilterApplied
                  ? "Try adjusting your search or filters"
                  : canManageSellers
                    ? "Add your first seller to get started"
                    : "No sellers yet"
              }
            />
          </div>
        )}
      </div>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {canManageSellers && isAddModalOpen && (
        <AddSeller
          onAdd={handleAddSellerSuccess}
          onClose={() => setIsAddModalOpen(false)}
          shops={safeShops}
        />
      )}

      {isViewModalOpen && selectedSeller && (
        <SellerDetails
          seller={selectedSeller}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedSeller(null);
          }}
        />
      )}

      {/* Advanced Filter Modal */}
      {isAdvFilterOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-900">
                Advanced Filter
              </h3>
              <button
                onClick={() => setIsAdvFilterOpen(false)}
                className="w-7 h-7 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white transition-colors shadow-sm"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6 flex flex-col gap-5">
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <FontAwesomeIcon
                    icon={faFilter}
                    className="text-blue-600 h-4 w-4"
                  />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">
                    Filter Sellers
                  </h4>
                  <p className="text-xs text-gray-600">
                    Select criteria to narrow down results
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="text-blue-500 h-3.5 w-3.5"
                  />
                  Filter by Date Added
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      From
                    </label>
                    <input
                      type="date"
                      value={tempFilters.startDate}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          startDate: e.target.value,
                        })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">
                      To
                    </label>
                    <input
                      type="date"
                      value={tempFilters.endDate}
                      onChange={(e) =>
                        setTempFilters({
                          ...tempFilters,
                          endDate: e.target.value,
                        })
                      }
                      className="w-full h-10 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <FontAwesomeIcon
                    icon={faStore}
                    className="text-blue-500 h-3.5 w-3.5"
                  />
                  Shop
                </label>
                <Select
                  value={tempFilters.shopFilter || "all"}
                  onValueChange={(val) =>
                    setTempFilters({ ...tempFilters, shopFilter: val })
                  }
                >
                  <SelectTrigger className="w-full h-10 border border-gray-300 rounded-lg bg-gray-50/50 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    <SelectValue placeholder="All Shops" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Shops</SelectItem>
                    {safeShops.map((shop) => (
                      <SelectItem key={shop.shopId} value={shop.shopId}>
                        {shop.shop_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
              <Button
                variant="outline"
                onClick={clearAdvFilters}
                className="border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none px-6"
              >
                Clear All
              </Button>
              <Button
                onClick={applyAdvFilters}
                className="bg-blue-800 hover:bg-blue-900 text-white px-6 shadow-sm"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSellerO;
